import React from "react";

// ── COREX · Plano de élite MODELO A · Distribución 01 (Funcional) ─────────────
// Generador semi-paramétrico BIM/CAD "Blueprint Dark":
//  • INTELIGENCIA DE CANTIDADES (v22.5): dibuja tantos bloques como indica la
//    columna CANT. de la tabla (2 baños → 2 recintos; 3 dormitorios → 3 bloques).
//    Cada bloque muestra su ÁREA UNITARIA (no el total acumulado).
//  • LÓGICA DE CIRCULACIÓN (v22.6): eje central (PASADIZO) que conecta el ingreso
//    con todos los ambientes; zonificación Social → Servicio → Privada; escalera
//    integrada al inicio del pasadizo; sin habitaciones ciegas (acceso directo).
// Estética: muros de doble línea 0.25/0.15, columnas sólidas con hatch + glow
// cian, librería de bloques CAD, cotas con líneas de extensión, corte A-A.

const CYAN = "#22d3ee", CYAN_HOT = "#00ffff", WALL = "#7dd3fc", INKL = "#e2e8f0";
const DIMC = "#93c5fd", BG = "#0b1420", FURN = "#cbd5e1";

const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Biblioteca de Élite: mapa tipo de ambiente → clave de bloque redibujable por la IA.
const TYPE_TO_BLOCK = { garage: "garage", bedmain: "bed", bedroom: "bed", living: "living", kitchen: "kitchen", laundry: "laundry", storage: "storage", store: "store", dining: "dining", bath: "bath", stairs: "stairs", patio: "patio", corridor: "corridor" };

// Huella REAL del mobiliario (m) — el bloque nunca se estira; mantiene proporción y deja espacio libre.
const FP = {
  garage: { w: 1.8, h: 4.5 }, bedmain: { w: 2.7, h: 2.8 }, bedroom: { w: 2.4, h: 2.6 },
  living: { w: 2.8, h: 2.6 }, kitchen: { w: 2.5, h: 2.6 }, dining: { w: 2.6, h: 2.7 },
  bath: { w: 1.6, h: 2.1 }, laundry: { w: 1.6, h: 1.8 }, storage: { w: 2.4, h: 2.6 },
  store: { w: 2.4, h: 3.0 }, stairs: { w: 1.0, h: 3.2 }, patio: { w: 3.2, h: 2.4 },
};

const LOTE_W = 7.0, LOTE_D = 20.0, W_EXT = 0.25, W_INT = 0.15;

// Clasificación por nombre de ambiente (tabla Bloque 4).
const RE = {
  garage: /cochera|garaje|estacion/i,
  sala: /sala|living|estar/i,
  comedor: /comedor/i,
  cocina: /cocina/i,
  lavanderia: /lavand/i,
  bodega: /bodega|dep[oó]sito|almac/i,
  tienda: /tienda|local\s*comerc/i,
  bano: /ba[ñn]o|ss\.?hh|servicio\s*higi/i,
  principal: /principal/i,
  dormitorio: /secundaria|habitaci[oó]n|dormitorio|cuarto|suite/i,
  escalera: /escalera/i,
  pasadizo: /pasadizo|circulaci/i,
  patio: /[aá]rea\s*libre|patio|jard[ií]n/i,
};

// tipo de mobiliario + rótulo corto por clase.
function classify(nombre = "") {
  if (RE.garage.test(nombre)) return { type: "garage", short: "COCHERA" };
  if (RE.sala.test(nombre)) return { type: "living", short: "SALA / LIVING" };
  if (RE.comedor.test(nombre)) return { type: "dining", short: "COMEDOR" };
  if (RE.cocina.test(nombre)) return { type: "kitchen", short: "COCINA" };
  if (RE.lavanderia.test(nombre)) return { type: "laundry", short: "LAVANDERÍA" };
  if (RE.bodega.test(nombre)) return { type: "storage", short: "BODEGA" };
  if (RE.tienda.test(nombre)) return { type: "store", short: "TIENDA" };
  if (RE.bano.test(nombre)) return { type: "bath", short: "BAÑO" };
  if (RE.principal.test(nombre)) return { type: "bedmain", short: "HAB. PRINCIPAL" };
  if (RE.dormitorio.test(nombre)) return { type: "bedroom", short: "DORMITORIO" };
  if (RE.escalera.test(nombre)) return { type: "stairs", short: "ESCALERA" };
  if (RE.pasadizo.test(nombre)) return { type: "corridor", short: "PASADIZO" };
  if (RE.patio.test(nombre)) return { type: "patio", short: "ÁREA LIBRE" };
  return { type: "generic", short: (nombre || "AMBIENTE").toUpperCase() };
}

// ── Librería de bloques CAD (mobiliario técnico) ──────────────────────────────
function Furniture({ type, rx, ry, rw, rh, sc, uso, stairsPlus, pro, custom }) {
  const g = { stroke: FURN, strokeWidth: 0.75, fill: "none" };
  const soft = "rgba(148,163,184,0.10)";
  if (rw < 9 || rh < 9) return null;
  // BIBLIOTECA DE ÉLITE: bloque IA a ESCALA REAL (huella fija en m), sin estirar, centrado (o adosado).
  if (custom && type !== "stairs") {
    const fp = FP[type] || { w: (rw / sc) * 0.8, h: (rh / sc) * 0.8 };
    let fw = fp.w * sc, fh = fp.h * sc;
    const k = Math.min(1, rw / fw, rh / fh); // solo reduce si no cabe; nunca amplifica
    fw *= k; fh *= k;
    const isBed = type === "bedmain" || type === "bedroom";
    // Camas adosadas al muro superior (deja circulación); resto centrado.
    const cx = rx + (rw - fw) / 2;
    const cy = isBed ? ry + Math.min(rh * 0.06, 6) : ry + (rh - fh) / 2;
    return <svg data-testid="vm-planta-a-ai-block" x={cx} y={cy} width={fw} height={fh} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" overflow="visible" dangerouslySetInnerHTML={{ __html: custom }} />;
  }
  if (type === "garage") {
    if (uso === "TIENDA") {
      return (
        <g>
          <rect x={rx + rw * 0.1} y={ry + rh * 0.12} width={rw * 0.8} height={rh * 0.76} fill="rgba(34,211,238,0.06)" stroke={CYAN} strokeWidth="0.7" strokeDasharray="4 3" />
          <text x={rx + rw / 2} y={ry + rh * 0.52} textAnchor="middle" fill={CYAN} fontSize={Math.min(9, rw / 10)} fontWeight="800" letterSpacing="0.6">LOCAL COMERCIAL</text>
          <text x={rx + rw / 2} y={ry + rh * 0.52 + 10} textAnchor="middle" fill="#7dd3fc" fontSize="6" fontWeight="700">MAMPARA VIDRIADA</text>
        </g>
      );
    }
    // ESCALA REAL BLOQUEADA (Ley de mobiliario): vehículo EXACTO 1.80 m (ancho) × 4.50 m (largo);
    // sólo se reduce uniformemente si el recinto es menor (nunca se deforma ni amplifica).
    let cw = 1.8 * sc, ch = 4.5 * sc;
    const kfit = Math.min(1, (rw - 6) / cw, (rh - 6) / ch);
    cw = Math.max(6, cw * kfit); ch = Math.max(12, ch * kfit);
    const cx = rx + rw / 2, cy = ry + rh * 0.52, x1 = cx - cw / 2, y1 = cy - ch / 2;
    return (
      <g {...g} fill={soft}>
        <rect x={x1} y={y1} width={cw} height={ch} rx={cw * 0.28} />
        <path d={`M ${x1 + cw * 0.14} ${y1 + ch * 0.28} Q ${cx} ${y1 - ch * 0.02} ${x1 + cw * 0.86} ${y1 + ch * 0.28}`} />
        <path d={`M ${x1 + cw * 0.14} ${y1 + ch * 0.72} Q ${cx} ${y1 + ch * 1.02} ${x1 + cw * 0.86} ${y1 + ch * 0.72}`} />
        {/* parabrisas / luneta */}
        <path d={`M ${x1 + cw * 0.2} ${y1 + ch * 0.30} L ${x1 + cw * 0.8} ${y1 + ch * 0.30} L ${x1 + cw * 0.72} ${y1 + ch * 0.40} L ${x1 + cw * 0.28} ${y1 + ch * 0.40} Z`} fill="rgba(34,211,238,0.10)" />
        <path d={`M ${x1 + cw * 0.28} ${y1 + ch * 0.60} L ${x1 + cw * 0.72} ${y1 + ch * 0.60} L ${x1 + cw * 0.8} ${y1 + ch * 0.70} L ${x1 + cw * 0.2} ${y1 + ch * 0.70} Z`} fill="rgba(34,211,238,0.10)" />
        <line x1={x1 + cw * 0.5} y1={y1 + ch * 0.30} x2={x1 + cw * 0.5} y2={y1 + ch * 0.44} />
        <line x1={x1 + cw * 0.5} y1={y1 + ch * 0.56} x2={x1 + cw * 0.5} y2={y1 + ch * 0.7} />
        {/* ruedas */}
        {[0.24, 0.76].map((fy) => [x1 - 1.5, x1 + cw - 2.5].map((wx, k) => (
          <rect key={`${fy}-${k}`} x={wx} y={y1 + ch * fy} width="4" height={ch * 0.14} rx="1.5" fill="rgba(148,163,184,0.35)" />
        )))}
        {/* espejos */}
        <rect x={x1 - 3} y={y1 + ch * 0.32} width="3" height="3.5" rx="1" fill={soft} />
        <rect x={x1 + cw} y={y1 + ch * 0.32} width="3" height="3.5" rx="1" fill={soft} />
        <ellipse cx={cx} cy={cy} rx={cw * 0.22} ry={ch * 0.1} strokeOpacity="0.5" />
      </g>
    );
  }
  if (type === "bedmain" || type === "bedroom") {
    const dbl = type === "bedmain";
    const bw = Math.min(rw * (dbl ? 0.6 : 0.68), (dbl ? 1.6 : 1.15) * sc), bh = Math.min(rh * 0.5, 2.0 * sc);
    const bx = rx + 4, by = ry + rh - bh - 4;
    return (
      <g {...g}>
        {pro && <rect x={rx + 3} y={ry + rh - bh - 7} width={rw - 6} height={bh + 4} rx="2" fill="rgba(34,211,238,0.05)" stroke="rgba(34,211,238,0.25)" strokeDasharray="3 2" />}
        <rect x={bx} y={by} width={bw} height={bh} rx="1.5" fill={soft} />
        {/* edredón con dobleces */}
        <rect x={bx} y={by} width={bw} height={bh * 0.24} fill="rgba(34,211,238,0.22)" />
        <path d={`M ${bx} ${by + bh * 0.24} Q ${bx + bw / 2} ${by + bh * 0.32} ${bx + bw} ${by + bh * 0.24}`} strokeOpacity="0.6" />
        <path d={`M ${bx} ${by + bh * 0.5} Q ${bx + bw / 2} ${by + bh * 0.58} ${bx + bw} ${by + bh * 0.5}`} strokeOpacity="0.4" />
        {dbl && <line x1={bx + bw / 2} y1={by + bh * 0.26} x2={bx + bw / 2} y2={by + bh} strokeOpacity="0.5" />}
        <rect x={bx + 2} y={by + 2} width={bw * (dbl ? 0.44 : 0.84) - 3} height={bh * 0.15} rx="1" />
        {dbl && <rect x={bx + bw * 0.52} y={by + 2} width={bw * 0.44 - 3} height={bh * 0.15} rx="1" />}
        {/* veladores (pro) */}
        {pro && <rect x={bx - 7} y={by} width="6" height="6" rx="1" fill={soft} />}
        {pro && dbl && <rect x={bx + bw + 1} y={by} width="6" height="6" rx="1" fill={soft} />}
        {/* ropero */}
        <rect x={rx + 3} y={ry + 3} width={rw - 6} height={Math.min(6, rh * 0.12)} fill="rgba(148,163,184,0.08)" />
        <line x1={rx + rw / 2} y1={ry + 3} x2={rx + rw / 2} y2={ry + 3 + Math.min(6, rh * 0.12)} />
      </g>
    );
  }
  if (type === "living") {
    const sw = Math.min(rw * 0.8, 2.6 * sc), cx = rx + rw / 2;
    const sh = Math.min(rh * 0.24, 0.9 * sc), sx = cx - sw / 2, sy = ry + rh - 4 - sh;
    return (
      <g {...g}>
        {pro && <rect x={cx - sw * 0.4} y={sy - sh * 1.6} width={sw * 0.8} height={sh * 2.1} rx="3" fill="rgba(34,211,238,0.05)" stroke="rgba(34,211,238,0.22)" strokeDasharray="3 2" />}
        <rect x={sx} y={sy} width={sw} height={sh} rx="2" fill={soft} />
        <rect x={sx} y={sy} width={sw} height={sh * 0.42} rx="2" />
        {/* cojines */}
        {[0.16, 0.5, 0.84].map((fx, i) => <rect key={i} x={sx + sw * fx - sw * 0.12} y={sy + sh * 0.12} width={sw * 0.22} height={sh * 0.26} rx="1.5" fill="rgba(34,211,238,0.14)" />)}
        {/* apoyabrazos */}
        <rect x={sx - 2} y={sy} width="3" height={sh} rx="1.5" fill={soft} />
        <rect x={sx + sw - 1} y={sy} width="3" height={sh} rx="1.5" fill={soft} />
        {/* mesa de centro */}
        <rect x={cx - sw * 0.18} y={sy - sh * 0.95} width={sw * 0.36} height={sh * 0.5} rx="1.5" fill="rgba(34,211,238,0.10)" />
        {pro && <rect x={sx - 4} y={sy - sh * 1.3} width="5" height="5" rx="2.5" fill="rgba(245,158,11,0.3)" />}
        <rect x={cx - sw * 0.22} y={ry + 4} width={sw * 0.44} height={3} fill={CYAN} opacity="0.55" />
      </g>
    );
  }
  if (type === "kitchen") {
    return (
      <g {...g}>
        <rect x={rx + 3} y={ry + 3} width={rw - 6} height={Math.min(7, rh * 0.2)} fill={soft} />
        <rect x={rx + 3} y={ry + 3} width={Math.min(7, rw * 0.2)} height={rh - 6} fill={soft} />
        {/* lavadero */}
        <rect x={rx + rw * 0.5} y={ry + 4.5} width={Math.min(10, rw * 0.24)} height={Math.min(4.5, rh * 0.13)} rx="1" />
        {/* cocina (4 hornillas) */}
        {[[0.26, 0.06], [0.34, 0.06], [0.26, 0.14], [0.34, 0.14]].map(([fx, fy], i) => <circle key={i} cx={rx + rw * fx} cy={ry + 3 + rh * fy} r="1.4" />)}
        {/* refrigeradora */}
        <rect x={rx + rw - 12} y={ry + rh - 16} width="9" height="13" rx="1.5" fill={soft} />
        <line x1={rx + rw - 12} y1={ry + rh - 9} x2={rx + rw - 3} y2={ry + rh - 9} strokeOpacity="0.5" />
        {/* horno / microondas (pro) */}
        {pro && <rect x={rx + 4} y={ry + rh - 10} width="10" height="7" rx="1" fill="rgba(34,211,238,0.10)" />}
      </g>
    );
  }
  if (type === "laundry") {
    return (
      <g {...g}>
        <rect x={rx + 3} y={ry + 3} width={rw - 6} height={Math.min(6, rh * 0.18)} fill={soft} />
        <rect x={rx + 4} y={ry + rh - 14} width="10" height="10" rx="1.5" />
        <circle cx={rx + 9} cy={ry + rh - 9} r="3" />
      </g>
    );
  }
  if (type === "storage" || type === "store") {
    return <g stroke={FURN} strokeWidth="0.6" fill="none"><rect x={rx + 4} y={ry + 4} width={rw - 8} height={rh - 8} strokeDasharray="3 2" opacity="0.6" /></g>;
  }
  if (type === "dining") {
    const cx = rx + rw / 2, cy = ry + rh / 2, tw = Math.min(rw * 0.5, 1.6 * sc), th = Math.min(rh * 0.5, 1.0 * sc);
    return (
      <g {...g}>
        <rect x={cx - tw / 2} y={cy - th / 2} width={tw} height={th} rx="2" fill={soft} />
        {[-1, 1].map((s) => <rect key={"c" + s} x={cx + s * (tw / 2 + 2) - 3} y={cy - 5} width="6" height="10" rx="1.5" />)}
        {[-1, 1].map((s) => <rect key={"r" + s} x={cx - 5} y={cy + s * (th / 2 + 2) - 3} width="10" height="6" rx="1.5" />)}
      </g>
    );
  }
  if (type === "bath") {
    return (
      <g {...g}>
        <g transform={`translate(${rx + 5}, ${ry + rh - 16})`}>
          <rect x="0" y="0" width="6.5" height="3" rx="1" fill={soft} />
          <ellipse cx="3.2" cy="8" rx="3.2" ry="4.2" fill={soft} />
        </g>
        <g transform={`translate(${rx + rw - 12}, ${ry + 4})`}>
          <rect x="0" y="0" width="8" height="5.5" rx="2" fill={soft} />
          <circle cx="4" cy="2.8" r="1.1" />
        </g>
        <rect x={rx + rw - 12} y={ry + rh - 14} width="9" height="10" rx="1" strokeDasharray="2 2" />
      </g>
    );
  }
  if (type === "stairs") return null; // ESCALERA = bloque rectangular limpio (rótulo genérico nombre + área)
  if (type === "patio") {
    return (
      <g stroke={CYAN} strokeWidth="0.7" fill="none">
        {[0.15, 0.4, 0.62, 0.85].map((fx, i) => (
          <g key={i} transform={`translate(${rx + rw * fx}, ${ry + rh * 0.55})`}>
            <circle cx="0" cy="0" r={4 + (i % 2) * 2} opacity="0.5" />
            <path d="M0 4 v6" /><path d="M-3 -2 q3 -5 3 -7 q0 2 3 7" opacity="0.6" />
          </g>
        ))}
      </g>
    );
  }
  return null;
}

const _NORM = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/habitacion|cuarto|suite/g, "dormitorio").replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
const _KW = ["principal", "dormitorio", "cocina", "comedor", "sala", "living", "estar", "bano", "cochera", "garaje", "escalera", "lavanderia", "bodega", "patio", "libre", "tienda", "pasadizo"];
function matchTarget(target, label) {
  const t = _NORM(target), l = _NORM(label);
  if (!t || !l) return false;
  const wantP = t.includes("principal"), hasP = l.includes("principal");
  if (wantP !== hasP && (t.includes("dormitorio") || l.includes("dormitorio"))) return false;
  const tn = (t.match(/\d+/) || [])[0], ln = (l.match(/\d+/) || [])[0];
  const share = _KW.some((k) => t.includes(k) && l.includes(k));
  if (l.includes(t) || t.includes(l) || share) { if (tn && ln) return tn === ln; return true; }
  return false;
}

export default function PlantaModeloAFuncional({ layers, ambientes = [], uso = "COCHERA", anchoReal, largoReal, xray = false, wallScale = 1, gridOn = true, stairsPlus = false, accent, edits, svgBlocks = {}, columns = null, colCounts = null, variant = "01" }) {
  const E = edits || { offsets: {}, extraCols: [], extraWalls: [], customDims: [], proRooms: [] };
  const sc = 26, x0 = 58, y0 = 52;
  const X = (m) => x0 + m * sc, Y = (m) => y0 + m * sc;
  const VW = x0 + LOTE_W * sc + 66, VH = y0 + LOTE_D * sc + 112;
  const colSz = 0.44 * sc;

  // ── CEREBRO DE DISTRIBUCIÓN (3 Leyes espaciales) — lee la tabla del Bloque 4 con CANT. ──
  const build = () => {
    // REGLA DE EXISTENCIA (Cero Fantasmas): la columna CANT. manda. Cant=0 → el ambiente NO se dibuja.
    const list = (ambientes || [])
      .map((a) => {
        const cls = classify(a.nombre || "");
        const rawCant = Number(a.cantidad);
        const cant = Number.isFinite(rawCant) ? Math.max(0, Math.round(rawCant)) : 1;
        const total = Number(a.m2) || 0;
        return { ...cls, nombre: a.nombre, cant, total, unit: cant ? total / cant : total, libre: a.libre };
      })
      // Patio y Pasadizo son circulación/servicio permanente; el resto exige cant>0 para existir.
      .filter((r) => r.cant > 0 || r.type === "patio" || r.type === "corridor");

    const patioAmb = list.find((r) => r.type === "patio");

    // ── Geometría base: 2 bandas laterales + PASADIZO FÍSICO central de 1.00 m (vacío). ──
    const IX0 = W_EXT, IX1 = LOTE_W - W_EXT, IW = IX1 - IX0;
    const wC = 1.0, colW = (IW - wC) / 2;                 // ~2.75 m de ancho útil por banda (Ley de escala real)
    const CX0 = IX0 + colW, CX1 = CX0 + wC;               // pasadizo centrado (eje 3.50)
    const LCX0 = IX0, RCX0 = CX1;

    // Patio (fondo) dibujado a su profundidad real (área / ancho interior).
    const dP = patioAmb ? Math.min(3.2, Math.max(1.2, patioAmb.unit / IW)) : 1.6;
    const bandTop = W_EXT, patioY1 = LOTE_D - W_EXT, patioY0 = patioY1 - dP;
    const usableD = patioY0 - bandTop;

    // ZONIFICACIÓN + AFINIDAD (P0): orden por zona (frente→fondo). Fachada: Cochera/Tienda (0-1);
    // Social (2-3); Servicio: escalera/cocina/lavandería (4-6); Privado: baños y dormitorios al fondo (8-10).
    const ZR = { garage: 0, store: 1, storage: 1, living: 2, dining: 3, stairs: 4, kitchen: 5, laundry: 6, generic: 7, bath: 8, bedmain: 9, bedroom: 10 };
    const rankOf = (t) => (ZR[t] != null ? ZR[t] : 7);

    // ── DESGLOSE POR UNIDAD: CANT>1 = un bloque por unidad con rótulo "[Nombre] 01/02/03…". ──
    const expand = [];
    list.forEach((r) => {
      if (r.type === "patio" || r.type === "corridor") return;
      const base = r.short === "BODEGA" ? "BODEGA / TIENDA" : r.short;
      for (let i = 0; i < r.cant; i++) {
        const label = r.cant > 1 ? `${base} ${String(i + 1).padStart(2, "0")}` : base;
        expand.push({ type: r.type, label, unit: r.unit, disp: r.unit, rank: rankOf(r.type) });
      }
    });

    // ═══ D02 · REDISEÑO LONGITUDINAL (v36.2) — paramétrico puro, no toca presupuesto ═══
    // ZONA 01 FRENTE (negocio) → ZONA 02 CENTRO (gran social abierto) → ZONA 03 FONDO (privado).
    const buildLongitudinal02 = () => {
      const thresholds = [];       // umbrales punteados (transición Sala/Comedor/Cocina, sin muros)
      const blk = [];
      const sorted = [...expand].sort((a, b) => (a.rank - b.rank) || a.type.localeCompare(b.type) || a.label.localeCompare(b.label));

      const front = sorted.filter((e) => ["garage", "store", "storage"].includes(e.type));
      const social = sorted.filter((e) => ["living", "dining", "kitchen"].includes(e.type));
      const stairsArr = sorted.filter((e) => e.type === "stairs");
      const backArr = sorted.filter((e) => !["garage", "store", "storage", "living", "dining", "kitchen", "stairs"].includes(e.type));

      const sum = (a) => a.reduce((s, e) => s + e.disp, 0);
      const dFrontNat = front.length ? Math.max(sum(front) / IW, 4.2) : 0;
      const dSocialNat = social.length ? Math.max(sum(social) / IW, 3.8) : 0;
      const dBackNat = backArr.length ? Math.max(sum(backArr) / IW, 3.8) : 0;
      const totalNat = dFrontNat + dSocialNat + dBackNat || 1;
      const kf = usableD / totalNat;                       // escala longitudinal → llena el lote sin whitespace
      const dFront = dFrontNat * kf, dSocial = dSocialNat * kf, dBack = dBackNat * kf;

      const yF0 = bandTop, yF1 = yF0 + dFront;
      const yS0 = yF1, yS1 = yS0 + dSocial;
      const yB0 = yS1;

      const split = (arr) => {                             // reparto equilibrado por bandas (ya viene agrupado por tipo)
        const L = [], R = []; let al = 0, ar = 0;
        arr.forEach((it) => { if (al <= ar) { L.push(it); al += it.disp; } else { R.push(it); ar += it.disp; } });
        return [L, R];
      };
      const layBandR = (arr, x, y0, y1, doorSide) => {
        let cy = y0, seenFace = false; const tot = sum(arr) || 1;
        arr.forEach((p, i) => {
          let h = (y1 - y0) * (p.disp / tot);
          if (i === arr.length - 1) h = y1 - cy;           // el último cierra la banda (sin huecos)
          const isFace = ["garage", "store", "storage"].includes(p.type);
          const streetAccess = isFace && !seenFace; if (isFace) seenFace = true;
          blk.push({
            x, y: cy, w: colW, h, label: p.label, area: p.unit, type: p.type,
            frontAccess: streetAccess ? (p.type === "garage" ? "garage" : "store") : null,
            door: { side: doorSide, off: 0.5 },
          });
          cy += h;
        });
      };

      // ── ZONA 01 · FRENTE (negocio): Cochera/Tienda en 2 bandas, separadas por el arranque del pasadizo ──
      if (front.length) { const [fL, fR] = split(front); layBandR(fL, LCX0, yF0, yF1, "r"); layBandR(fR, RCX0, yF0, yF1, "l"); }

      // ── ZONA 02 · CENTRO (Gran Social abierto): SALA+COMEDOR+COCINA sin muros; subzonas por umbrales punteados ──
      if (social.length) {
        blk.push({ x: IX0, y: yS0, w: IW, h: dSocial, type: "opensocial", label: null });   // fondo abierto (fill sin muros)
        let sy = yS0; const aSoc = sum(social) || 1;
        social.forEach((e, idx) => {
          let h = dSocial * (e.disp / aSoc);
          if (idx === social.length - 1) h = yS1 - sy;
          if (idx > 0) thresholds.push(sy);                // línea punteada de transición (no es muro)
          blk.push({ x: IX0, y: sy, w: IW, h, type: e.type, label: e.label, area: e.unit, noWall: true, open: true });
          sy += h;
        });
      }

      // ── ESCALERA: bloque limpio, lateral al pasadizo, arranque ABIERTO (openSide) sin muro que lo bloquee ──
      if (stairsArr.length && dSocial > 1.2) {
        const st = stairsArr[0], stW = 1.1, stH = Math.min(dSocial * 0.9, 3.0);
        blk.push({ x: CX0 - stW, y: yS0 + Math.max(0, (dSocial - stH) * 0.15), w: stW, h: stH, type: "stairs", label: st.label, area: st.unit, openSide: "r" });
      }

      // ── ZONA 03 · FONDO (privado): dormitorios + baño principal agrupados en 2 bandas ──
      if (backArr.length) { const [bL, bR] = split(backArr); layBandR(bL, LCX0, yB0, patioY0, "r"); layBandR(bR, RCX0, yB0, patioY0, "l"); }

      // ── PASADIZO FÍSICO 1.00 m: atraviesa las 3 zonas de forma fluida (vacío, sin muros) ──
      blk.push({ x: CX0, y: bandTop, w: wC, h: patioY0 - bandTop, label: "PASADIZO", type: "corridor" });

      // ── PATIO (fondo) con mampara de vidrio ──
      blk.push({ x: IX0, y: patioY0, w: IW, h: dP, label: patioAmb ? patioAmb.short : "ÁREA LIBRE", area: patioAmb ? patioAmb.unit : null, type: "patio", libre: true });

      const patioArea = patioAmb ? patioAmb.unit : 0;
      const techada = expand.reduce((s, r) => s + r.unit, 0);
      return {
        blocks: blk, techada, patioArea, dF: dFront || 2, spineY0: bandTop, spineY1: patioY0, dP,
        colW, CX0, CX1, patioY0, IX0, IX1, IW, thresholds, frontIsVehicular: front.length > 0,
        aMid: bandTop + (dFront || 2) / 2,
        bMid: social.length ? yS0 + dSocial / 2 : null,
        cMid: yB0 + dBack / 2,
        socialY1: yS1,
      };
    };

    // ── VARIANTES DE TABIQUERÍA (clonan la calidad gráfica de la DISTRIB. 01; no tocan presupuesto) ──
    if (variant === "02") return buildLongitudinal02();
    if (variant === "03") {
      // PREMIUM: prioriza la Habitación Principal (mayor tamaño + baño privado) y compacta las secundarias.
      expand.forEach((e) => {
        if (e.type === "bedmain") { e.disp = e.unit * 1.55; e.label = `${e.label} + BAÑO PRIV.`; }
        else if (e.type === "bedroom") { e.disp = e.unit * 0.82; }
      });
    }
    const frontIsVehicular = list.some((r) => r.type === "garage" || r.type === "store" || r.type === "storage");

    // AGRUPAR POR TIPO dentro de cada zona: mismo rank → mismo tipo consecutivo (Cochera 01/02 juntas,
    // Dormitorio 01/02 juntos) para que las unidades del mismo tipo queden CONTIGUAS en su banda.
    expand.sort((a, b) => (a.rank - b.rank) || a.type.localeCompare(b.type) || a.label.localeCompare(b.label));

    // ── APILAMIENTO LONGITUDINAL (P0): cocheras en la banda izquierda, tiendas en la derecha.
    // Si no caben lado a lado en el ancho de banda, se colocan UNO TRAS OTRO (Fila 1 al frente con
    // salida a la calle, Fila 2 detrás), conservando su proporción rectangular habitable (sin aplastar).
    const garages = expand.filter((e) => e.type === "garage");
    const stores = expand.filter((e) => e.type === "store" || e.type === "storage");
    const rest = expand.filter((e) => e.type !== "garage" && e.type !== "store" && e.type !== "storage");

    const bandL = [...garages], bandR = [...stores];
    let aL = garages.reduce((s, g) => s + g.disp, 0);
    let aR = stores.reduce((s, g) => s + g.disp, 0);
    rest.forEach((it) => { if (aL <= aR) { bandL.push(it); aL += it.disp; } else { bandR.push(it); aR += it.disp; } });

    const hRealL = aL / colW, hRealR = aR / colW;
    const scaleL = bandL.length ? Math.min(1.9, usableD / Math.max(hRealL, 0.001)) : 1;
    const scaleR = bandR.length ? Math.min(1.9, usableD / Math.max(hRealR, 0.001)) : 1;

    const blocks = [];
    const layBand = (arr, x, doorSide, sBand) => {
      let cy = bandTop, seenFace = false;
      arr.forEach((p, i) => {
        let h = (p.disp / colW) * sBand;
        if (i === arr.length - 1) h = patioY0 - cy;   // el último recinto CIERRA la banda (sin whitespace)
        const isFace = p.type === "garage" || p.type === "store" || p.type === "storage";
        const streetAccess = isFace && !seenFace;      // solo la PRIMERA fila da a la calle (portón/mampara)
        if (isFace) seenFace = true;
        blocks.push({
          x, y: cy, w: colW, h, label: p.label, area: p.unit, type: p.type,
          frontAccess: streetAccess ? (p.type === "garage" ? "garage" : "store") : null,
          door: { side: doorSide, off: 0.5 },
        });
        cy += h;
      });
    };
    layBand(bandL, LCX0, "r", scaleL);
    layBand(bandR, RCX0, "l", scaleR);

    // ── PASADIZO FÍSICO: franja central de 1.00 m, VACÍA, del frente al patio. ──
    blocks.push({ x: CX0, y: bandTop, w: wC, h: (patioY0 - bandTop), label: "PASADIZO", type: "corridor" });

    // ── PATIO (fondo) — acceso con mampara de vidrio (Ley 3) ──
    blocks.push({ x: IX0, y: patioY0, w: IW, h: dP, label: variant === "03" ? "TERRAZA JARDÍN" : (patioAmb ? patioAmb.short : "ÁREA LIBRE"), area: patioAmb ? patioAmb.unit : null, type: "patio", libre: true });

    const patioArea = patioAmb ? patioAmb.unit : 0;
    const techada = expand.reduce((s, r) => s + r.unit, 0);
    const dF = Math.max(bandL[0] ? (bandL[0].disp / colW) * scaleL : 0, bandR[0] ? (bandR[0].disp / colW) * scaleR : 0.001);
    return {
      blocks, techada, patioArea, dF, spineY0: bandTop, spineY1: patioY0, dP, colW, CX0, CX1, patioY0, IX0, IX1, IW, frontIsVehicular,
      aMid: bandTop + dF / 2,
      bMid: bandTop + usableD * 0.45,
      cMid: bandTop + usableD * 0.8,
      socialY1: bandTop + dF,
    };
  };

  const model = build();
  // ── SISTEMA DE ALERTA DE EXCESO (Regla del 100%): utilización = (techada + patio) / terreno. ──
  const terrenoReal = (Number(anchoReal) || 0) * (Number(largoReal) || 0);
  const utilPct = terrenoReal > 0 ? ((model.techada || 0) + (model.patioArea || 0)) / terrenoReal * 100 : 0;
  const areaExcedida = utilPct > 100.0001;
  // Aplica ediciones geométricas de la IA (mover ambiente) sobre los bloques generados.
  const offEntries = Object.entries(E.offsets || {});
  const blocks = model.blocks.map((b) => {
    if (!b.label || !offEntries.length) return b;
    const hit = offEntries.find(([k]) => matchTarget(k, b.label));
    if (!hit) return b;
    const { dx = 0, dy = 0 } = hit[1] || {};
    const nx = Math.max(W_EXT, Math.min(LOTE_W - W_EXT - b.w, b.x + dx));
    const ny = Math.max(W_EXT, Math.min(LOTE_D - W_EXT - b.h, b.y + dy));
    return { ...b, x: nx, y: ny, moved: true };
  });
  const isPro = (label) => (E.proRooms || []).some((k) => matchTarget(k, label));

  const doorArc = (b) => {
    if (!b.door) return null;
    const rx = X(b.x), ry = Y(b.y), rw = b.w * sc, rh = b.h * sc;
    const r = Math.min(13, Math.min(rw, rh) * 0.4);
    if (r < 4) return null;
    const { side, off } = b.door;
    let px, py, dx, dy, sweep;
    if (side === "r") { px = rx + rw; py = ry + rh * off; dx = -r; dy = r; sweep = 0; }
    else if (side === "l") { px = rx; py = ry + rh * off; dx = r; dy = r; sweep = 1; }
    else if (side === "b") { px = rx + rw * off; py = ry + rh; dx = r; dy = -r; sweep = 1; }
    else { px = rx + rw * off; py = ry; dx = r; dy = r; sweep = 0; }
    const isV = side === "r" || side === "l";
    return (
      <g stroke={WALL} strokeWidth="0.8" fill="none">
        <line x1={px} y1={py} x2={isV ? px : px + dx} y2={isV ? py + dy : py} />
        <path d={`M ${isV ? px : px + dx} ${isV ? py + dy : py} A ${r} ${r} 0 0 ${sweep} ${px + dx} ${isV ? py : py + dy}`} strokeDasharray="2.5 2" />
      </g>
    );
  };

  const windowsFor = (b) => {
    if (!["living", "bedmain", "bedroom", "kitchen", "dining", "bath", "laundry"].includes(b.type)) return null;
    const rx = X(b.x), ry = Y(b.y), rw = b.w * sc, rh = b.h * sc, eps = 0.03, out = [];
    const win = (x1, y1, x2, y2, k) => (
      <g key={k} stroke={CYAN} strokeWidth="1.1">
        <line x1={x1} y1={y1} x2={x2} y2={y2} />
        {Math.abs(x2 - x1) > Math.abs(y2 - y1)
          ? <line x1={x1} y1={y1 + 1.7} x2={x2} y2={y2 + 1.7} strokeOpacity="0.7" />
          : <line x1={x1 + 1.7} y1={y1} x2={x2 + 1.7} y2={y2} strokeOpacity="0.7" />}
      </g>
    );
    if (Math.abs(b.x - W_EXT) < eps && rh > 24) out.push(win(rx, ry + rh * 0.32, rx, ry + rh * 0.68, "l"));
    if (Math.abs(b.x + b.w - (LOTE_W - W_EXT)) < eps && rh > 24) out.push(win(rx + rw, ry + rh * 0.32, rx + rw, ry + rh * 0.68, "r"));
    if (Math.abs(b.y - W_EXT) < eps && rw > 24) out.push(win(rx + rw * 0.32, ry, rx + rw * 0.68, ry, "t"));
    return out;
  };

  // ── Cotas profesionales (cadenas en los 4 lados, ticks 45° + líneas de extensión) ──
  const hPts = [0, model.CX0, model.CX1, LOTE_W];
  const vPts = [0, W_EXT + model.dF, model.spineY1, LOTE_D];
  const TK = 2.6;

  const chainH = (yb, above) => (
    <g stroke={DIMC} strokeWidth="0.7" fill={DIMC} data-testid={above ? "vm-planta-a-dim-top" : "vm-planta-a-dim-bottom"}>
      <line x1={X(0)} y1={yb} x2={X(LOTE_W)} y2={yb} />
      {hPts.map((p, i) => (
        <g key={i}>
          <line x1={X(p) - TK} y1={yb - TK} x2={X(p) + TK} y2={yb + TK} strokeWidth="0.9" />
          <line x1={X(p)} y1={yb} x2={X(p)} y2={above ? Y(0) : Y(LOTE_D)} strokeOpacity="0.22" strokeDasharray="2 3" />
        </g>
      ))}
      {hPts.slice(0, -1).map((p, i) => (
        <text key={"s" + i} x={(X(p) + X(hPts[i + 1])) / 2} y={above ? yb - 4 : yb + 9} textAnchor="middle" stroke="none" fontSize="6.2" fontWeight="700">{n2(hPts[i + 1] - p)}</text>
      ))}
      <text x={(X(0) + X(LOTE_W)) / 2} y={above ? yb - 14 : yb + 19} textAnchor="middle" stroke="none" fontSize="8" fontWeight="800">{n2(LOTE_W)} m</text>
    </g>
  );

  const chainV = (xb, left) => (
    <g stroke={DIMC} strokeWidth="0.7" fill={DIMC} data-testid={left ? "vm-planta-a-dim-left" : "vm-planta-a-dim-right"}>
      <line x1={xb} y1={Y(0)} x2={xb} y2={Y(LOTE_D)} />
      {vPts.map((p, i) => (
        <g key={i}>
          <line x1={xb - TK} y1={Y(p) - TK} x2={xb + TK} y2={Y(p) + TK} strokeWidth="0.9" />
          <line x1={xb} y1={Y(p)} x2={left ? X(0) : X(LOTE_W)} y2={Y(p)} strokeOpacity="0.22" strokeDasharray="2 3" />
        </g>
      ))}
      {vPts.slice(0, -1).map((p, i) => {
        const yc = (Y(p) + Y(vPts[i + 1])) / 2, xt = left ? xb - 4 : xb + 5;
        return <text key={"s" + i} x={xt} y={yc} textAnchor="middle" stroke="none" fontSize="6" fontWeight="700" transform={`rotate(-90 ${xt} ${yc})`}>{n2(vPts[i + 1] - p)}</text>;
      })}
      <text x={left ? xb - 15 : xb + 16} y={(Y(0) + Y(LOTE_D)) / 2} textAnchor="middle" stroke="none" fontSize="8" fontWeight="800" transform={`rotate(-90 ${left ? xb - 15 : xb + 16} ${(Y(0) + Y(LOTE_D)) / 2})`}>{n2(LOTE_D)} m</text>
    </g>
  );

  const dimTop = () => chainH(y0 - 24, true);
  const dimBottom = () => chainH(Y(LOTE_D) + 26, false);
  const dimLeft = () => chainV(x0 - 28, true);
  const dimRight = () => chainV(X(LOTE_W) + 32, false);

  // ── Columnas estructurales: reflejo fiel de la Ley de Ejes (backend) ──────────
  // Posiciones en metros reales del terreno → mapeadas al pliego curado (esquinas cierran el circuito).
  const aR = Number(anchoReal) || LOTE_W, lR = Number(largoReal) || LOTE_D;
  const mapMX = (mx) => (aR > 0 ? (mx / aR) * LOTE_W : mx);
  const mapMY = (my) => (lR > 0 ? (my / lR) * LOTE_D : my);
  const CAT_SIZE = { "C-01": 0.36, "C-02": 0.44, "C-03": 0.54 }; // lado dibujado (m) por jerarquía
  const colDraw = (columns && columns.length)
    ? columns.map((c) => ({ x: mapMX(c.x), y: mapMY(c.y), cat: c.cat, s: (CAT_SIZE[c.cat] || 0.42) * sc }))
    : (() => {
        const out = []; const csx = [0.4, 3.5, model.CX0, model.CX1, 6.6];
        const csy = [0.4, W_EXT + model.dF, (model.spineY0 + model.spineY1) / 2, model.spineY1, 19.6];
        csx.forEach((mx) => csy.forEach((my) => out.push({ x: mx, y: my, cat: "C-03", s: colSz })));
        return out;
      })();

  // Cotas personalizadas añadidas por la IA (por lado o por coordenadas).
  const renderCustomDim = (dim, i) => {
    if (dim.side) {
      const off = 18;
      if (dim.side === "left" || dim.side === "right") {
        const x = dim.side === "left" ? x0 - 42 : X(LOTE_W) + off + 8;
        return (
          <g key={"cd" + i} stroke="#fbbf24" strokeWidth="0.8" fill="#fbbf24" data-testid="vm-planta-a-xdim">
            <line x1={x} y1={Y(0)} x2={x} y2={Y(LOTE_D)} />
            <line x1={x - 3} y1={Y(0)} x2={x + 3} y2={Y(0)} /><line x1={x - 3} y1={Y(LOTE_D)} x2={x + 3} y2={Y(LOTE_D)} />
            <text x={x - 3} y={(Y(0) + Y(LOTE_D)) / 2} textAnchor="middle" stroke="none" fontSize="6.5" fontWeight="800" transform={`rotate(-90 ${x - 3} ${(Y(0) + Y(LOTE_D)) / 2})`}>{dim.label || `${n2(LOTE_D)} m`}</text>
          </g>
        );
      }
      const y = dim.side === "top" ? y0 - 34 : Y(LOTE_D) + off;
      return (
        <g key={"cd" + i} stroke="#fbbf24" strokeWidth="0.8" fill="#fbbf24" data-testid="vm-planta-a-xdim">
          <line x1={X(0)} y1={y} x2={X(LOTE_W)} y2={y} />
          <line x1={X(0)} y1={y - 3} x2={X(0)} y2={y + 3} /><line x1={X(LOTE_W)} y1={y - 3} x2={X(LOTE_W)} y2={y + 3} />
          <text x={(X(0) + X(LOTE_W)) / 2} y={y - 4} textAnchor="middle" stroke="none" fontSize="6.5" fontWeight="800">{dim.label || `${n2(LOTE_W)} m`}</text>
        </g>
      );
    }
    const d = Math.hypot((dim.x2 - dim.x1), (dim.y2 - dim.y1));
    const mx = (X(dim.x1) + X(dim.x2)) / 2, my = (Y(dim.y1) + Y(dim.y2)) / 2;
    return (
      <g key={"cd" + i} stroke="#fbbf24" strokeWidth="0.8" fill="#fbbf24" data-testid="vm-planta-a-xdim">
        <line x1={X(dim.x1)} y1={Y(dim.y1)} x2={X(dim.x2)} y2={Y(dim.y2)} />
        <text x={mx} y={my - 3} textAnchor="middle" stroke="none" fontSize="6.5" fontWeight="800">{dim.label || `${n2(d)} m`}</text>
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block select-none" data-testid="vm-planta-modelo-a" role="img" aria-label="Plano MODELO A Funcional">
      <defs>
        <pattern id="maFine" width="13" height="13" patternUnits="userSpaceOnUse"><path d="M13 0H0V13" fill="none" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="0.5" /></pattern>
        <pattern id="maCoarse" width="65" height="65" patternUnits="userSpaceOnUse"><path d="M65 0H0V65" fill="none" stroke="#38bdf8" strokeOpacity="0.09" strokeWidth="0.7" /></pattern>
        <pattern id="maHatch" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="4" stroke="#67e8f9" strokeWidth="0.9" /></pattern>
        <filter id="maGlow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
      </defs>

      <rect x="0" y="0" width={VW} height={VH} fill={BG} />
      {/* Regla del 100%: fondo rojo tenue persistente mientras el área esté excedida. */}
      {areaExcedida && <rect x="0" y="0" width={VW} height={VH} fill="rgba(255,0,0,0.05)" data-testid="vm-area-excedida-overlay" />}
      {gridOn && <rect x="0" y="0" width={VW} height={VH} fill="url(#maFine)" />}
      {gridOn && <rect x="0" y="0" width={VW} height={VH} fill="url(#maCoarse)" />}

      {/* Las cotas (ACOT) se dibujan al final para quedar SIEMPRE sobre el glow de columnas (jerarquía técnica). */}

      {/* ── CAPA ARQ: poché perimetral + ambientes (muros interiores 0.15) ── */}
      {layers.ARQ && (
        <g opacity={xray ? 0.4 : 1}>
          <g fill={xray ? "rgba(125,211,252,0.05)" : "rgba(125,211,252,0.16)"} stroke={WALL} strokeWidth={0.8 * wallScale}>
            <rect x={X(0)} y={Y(0)} width={LOTE_W * sc} height={W_EXT * sc} />
            <rect x={X(0)} y={Y(LOTE_D - W_EXT)} width={LOTE_W * sc} height={W_EXT * sc} />
            <rect x={X(0)} y={Y(0)} width={W_EXT * sc} height={LOTE_D * sc} />
            <rect x={X(LOTE_W - W_EXT)} y={Y(0)} width={W_EXT * sc} height={LOTE_D * sc} />
          </g>
          {blocks.filter((b) => !b.noWall).map((b, i) => {
            const isPatio = b.type === "patio", isCorr = b.type === "corridor";
            const fill = isPatio ? "rgba(34,211,238,0.05)" : isCorr ? "rgba(148,163,184,0.06)" : "rgba(125,211,252,0.035)";
            const x1 = X(b.x), y1 = Y(b.y), x2 = X(b.x) + b.w * sc, y2 = Y(b.y) + b.h * sc;
            const sw = W_INT * sc * wallScale;
            // Pasadizo Sagrado: circulación fluida (sin muros que la encierren).
            if (isCorr) return <rect key={"w" + i} x={x1} y={y1} width={b.w * sc} height={b.h * sc} fill={fill} stroke="none" />;
            // D02 · Gran Social: fondo abierto continuo (sin muros divisorios internos).
            if (b.type === "opensocial") return <rect key={"w" + i} x={x1} y={y1} width={b.w * sc} height={b.h * sc} fill="rgba(34,211,238,0.05)" stroke="none" />;
            // LEY 2 · Escalera Libre: arranque abierto al pasadizo (se omite el muro de ese lado).
            if (b.openSide) {
              const sides = { t: [x1, y1, x2, y1], b: [x1, y2, x2, y2], l: [x1, y1, x1, y2], r: [x2, y1, x2, y2] };
              return (
                <g key={"w" + i}>
                  <rect x={x1} y={y1} width={b.w * sc} height={b.h * sc} fill={fill} stroke="none" />
                  {Object.entries(sides).filter(([k]) => k !== b.openSide).map(([k, [a, bb, c, d]]) => (
                    <line key={k} x1={a} y1={bb} x2={c} y2={d} stroke={WALL} strokeWidth={sw} strokeOpacity="0.9" />
                  ))}
                </g>
              );
            }
            return <rect key={"w" + i} x={x1} y={y1} width={b.w * sc} height={b.h * sc} fill={fill} stroke={WALL} strokeWidth={sw} strokeOpacity="0.9" strokeDasharray={isPatio ? "6 4" : "none"} />;
          })}
          {/* LEY 3 · Apertura al Patio: mampara técnica de vidrio (doble línea fina cian, sin obstrucción visual) */}
          <g data-testid="vm-planta-a-mampara-patio" stroke={CYAN} fill="none">
            <line x1={X(model.IX0)} y1={Y(model.patioY0) - 1.2} x2={X(model.IX1)} y2={Y(model.patioY0) - 1.2} strokeWidth="0.8" strokeOpacity="0.9" />
            <line x1={X(model.IX0)} y1={Y(model.patioY0) + 1.2} x2={X(model.IX1)} y2={Y(model.patioY0) + 1.2} strokeWidth="0.8" strokeOpacity="0.9" />
            {[0.22, 0.42, 0.62, 0.82].map((f, i) => (
              <line key={i} x1={X(model.IX0) + model.IW * sc * f} y1={Y(model.patioY0) - 1.2} x2={X(model.IX0) + model.IW * sc * f} y2={Y(model.patioY0) + 1.2} strokeWidth="0.6" strokeOpacity="0.55" />
            ))}
          </g>
          {/* D02 · UMBRALES: líneas punteadas muy finas en el suelo (transición Sala/Comedor/Cocina, SIN muros) */}
          {(model.thresholds || []).map((ty, i) => (
            <line key={"th" + i} x1={X(model.IX0)} y1={Y(ty)} x2={X(model.IX1)} y2={Y(ty)} stroke={CYAN} strokeWidth="0.5" strokeOpacity="0.45" strokeDasharray="1.4 3.2" data-testid="vm-planta-a-umbral" />
          ))}
          {/* Resaltado de ambientes movidos por la IA */}
          {blocks.filter((b) => b.moved).map((b, i) => (
            <rect key={"mv" + i} x={X(b.x)} y={Y(b.y)} width={b.w * sc} height={b.h * sc} fill="none" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="4 3" data-testid="vm-planta-a-moved" />
          ))}
          {/* Muros duplicados por la IA */}
          {(E.extraWalls || []).map((w, i) => (
            <line key={"xw" + i} x1={X(w.x1)} y1={Y(w.y1)} x2={X(w.x2)} y2={Y(w.y2)} stroke={WALL} strokeWidth={W_INT * sc * wallScale} strokeLinecap="round" data-testid="vm-planta-a-xwall" />
          ))}
        </g>
      )}

      {/* ── CAPA MOB: mobiliario + puertas + ventanas ── */}
      {layers.MOB && layers.ARQ && (
        <g>
          {blocks.filter((b) => b.type !== "corridor").map((b, i) => (
            <g key={"f" + i}>
              <Furniture type={b.type} rx={X(b.x)} ry={Y(b.y)} rw={b.w * sc} rh={b.h * sc} sc={sc} uso={uso} stairsPlus={stairsPlus} pro={isPro(b.label)} custom={svgBlocks[TYPE_TO_BLOCK[b.type]]} />
              {doorArc(b)}
              {windowsFor(b)}
            </g>
          ))}
          {/* Portón (Cochera) / Mampara (Tienda) POR UNIDAD, al ras del retiro frontal — ley comercial */}
          {blocks.filter((b) => b.frontAccess).map((b, i) => (
            <rect key={"fa" + i} x={X(b.x) + 2} y={Y(b.y) - 1} width={b.w * sc - 4} height={2.4}
              fill={b.frontAccess === "store" ? "#7dd3fc" : CYAN} opacity="0.9"
              data-testid={b.frontAccess === "store" ? "vm-mampara-frontal" : "vm-porton-frontal"} />
          ))}
          {/* puerta peatonal al pasadizo */}
          <g stroke={WALL} strokeWidth="0.8" fill="none">
            <line x1={X(model.CX0 + 0.1)} y1={Y(0.25)} x2={X(model.CX0 + 0.1)} y2={Y(1.05)} />
            <path d={`M ${X(model.CX0 + 0.1)} ${Y(1.05)} A ${0.8 * sc} ${0.8 * sc} 0 0 0 ${X(model.CX0 + 0.1) + 0.8 * sc} ${Y(0.25)}`} strokeDasharray="2.5 2" />
          </g>
        </g>
      )}

      {/* ── CAPA EST: columnas C-01/C-02/C-03 (Ley de Ejes) con hatch + glow ── */}
      {layers.EST && (
        <g filter="url(#maGlow)">
          {colDraw.map((c, i) => (
            <g key={i} data-testid="vm-planta-a-col" data-cat={c.cat}>
              <rect x={X(c.x) - c.s / 2} y={Y(c.y) - c.s / 2} width={c.s} height={c.s} fill="url(#maHatch)" stroke={CYAN_HOT} strokeWidth="1.5" />
              <rect x={X(c.x) - c.s / 2} y={Y(c.y) - c.s / 2} width={c.s} height={c.s} fill="rgba(0,255,255,0.24)" />
              <rect x={X(c.x) - c.s * 0.2} y={Y(c.y) - c.s * 0.2} width={c.s * 0.4} height={c.s * 0.4} fill={CYAN_HOT} />
            </g>
          ))}
          {/* Columnas duplicadas por la IA (acento ámbar) */}
          {(E.extraCols || []).map((c, i) => (
            <g key={"xc" + i} data-testid="vm-planta-a-xcol">
              <rect x={X(c.x) - colSz / 2} y={Y(c.y) - colSz / 2} width={colSz} height={colSz} fill="url(#maHatch)" stroke="#f59e0b" strokeWidth="1.1" />
              <rect x={X(c.x) - colSz / 2} y={Y(c.y) - colSz / 2} width={colSz} height={colSz} fill="rgba(245,158,11,0.18)" />
            </g>
          ))}
        </g>
      )}

      {/* ── Rótulos: nombre + ÁREA UNITARIA (dinámica de la tabla) ── */}
      {layers.ARQ && (
        <g textAnchor="middle">
          {blocks.map((b, i) => {
            if (b.type === "corridor") {
              const cx = X(b.x + b.w / 2), cy = Y(b.y + b.h * 0.72);
              return <text key={"t" + i} x={cx} y={cy} fill="#94a3b8" fontSize="6" fontWeight="800" letterSpacing="1" transform={`rotate(-90 ${cx} ${cy})`}>{b.label}</text>;
            }
            if (b.type === "opensocial" || (!b.label && b.area == null)) return null;
            const cx = X(b.x + b.w / 2), cy = Y(b.y + b.h / 2);
            const small = b.w < 1.5 || b.h < 1.5;
            const fs = small ? 5.2 : Math.min(8, b.w * 2.6);
            return (
              <g key={"t" + i}>
                <text x={cx} y={cy - (b.area != null ? 3 : 0)} fill={b.type === "patio" ? "#7dd3fc" : INKL} fontSize={fs} fontWeight="800" letterSpacing="0.3">{b.label}</text>
                {b.area != null && <text x={cx} y={cy + 6} fill={CYAN} fontSize={small ? 4.8 : 6.4} fontWeight="800">{n2(b.area)} m²</text>}
              </g>
            );
          })}
        </g>
      )}

      {/* Etiquetas de zonificación por profundidad (Ley 2: A frente · B social · C privada/servicio) */}
      {layers.ACOT && (
        <g fontSize="5.4" fontWeight="800" letterSpacing="0.5" fill="#38bdf8">
          <text x={X(LOTE_W) + 3} y={Y(model.aMid)} transform={`rotate(90 ${X(LOTE_W) + 3} ${Y(model.aMid)})`}>ZONA A · FRENTE / INGRESO</text>
          {model.bMid != null && <text x={X(LOTE_W) + 3} y={Y(model.bMid)} transform={`rotate(90 ${X(LOTE_W) + 3} ${Y(model.bMid)})`}>ZONA B · SOCIAL</text>}
          <text x={X(LOTE_W) + 3} y={Y(model.cMid)} transform={`rotate(90 ${X(LOTE_W) + 3} ${Y(model.cMid)})`}>ZONA C · PRIVADA / SERVICIO</text>
        </g>
      )}

      {/* Línea de corte A-A */}
      {layers.ACOT && (
        <g stroke="#f59e0b" strokeWidth="1" fill="#f59e0b">
          <line x1={X(-0.1)} y1={Y(model.spineY0 + (model.spineY1 - model.spineY0) * 0.45)} x2={X(LOTE_W + 0.1)} y2={Y(model.spineY0 + (model.spineY1 - model.spineY0) * 0.45)} strokeDasharray="7 3 2 3" />
          <text x={X(-0.1)} y={Y(model.spineY0 + (model.spineY1 - model.spineY0) * 0.45) - 3} fontSize="7" fontWeight="800" stroke="none">A</text>
          <text x={X(LOTE_W + 0.1)} y={Y(model.spineY0 + (model.spineY1 - model.spineY0) * 0.45) - 3} textAnchor="end" fontSize="7" fontWeight="800" stroke="none">A</text>
        </g>
      )}

      {/* ── COTAS (ACOT) + INVENTARIO al final = por encima del glow de columnas ── */}
      {layers.ACOT && dimTop()}
      {layers.ACOT && dimBottom()}
      {layers.ACOT && dimLeft()}
      {layers.ACOT && dimRight()}
      {layers.ACOT && (E.customDims || []).length > 0 && <g data-testid="vm-planta-a-xdims">{(E.customDims || []).map(renderCustomDim)}</g>}

      {/* Inventario estructural (reflejo fiel de la Ley de Ejes) */}
      {colCounts && (
        <g data-testid="vm-planta-a-col-inventory" transform={`translate(${X(1.55)}, ${Y(LOTE_D) + 40})`} fontWeight="800">
          <rect x="0" y="0" width="152" height="30" rx="3" fill="rgba(8,34,44,0.9)" stroke="#0b3a4a" strokeWidth="0.7" />
          <text x="7" y="12" fill="#67e8f9" fontSize="6" stroke="none">INVENTARIO ESTRUCTURAL · EJES Y-Y @4.00 · X-X @3.50 m</text>
          <text x="7" y="24" fill="#e2e8f0" fontSize="7.2" stroke="none">C-01 Esq: {colCounts["C-01"] || 0}   C-02 Lat: {colCounts["C-02"] || 0}   C-03 Cent: {colCounts["C-03"] || 0}   Σ {(colCounts["C-01"] || 0) + (colCounts["C-02"] || 0) + (colCounts["C-03"] || 0)}</text>
        </g>
      )}

      {/* Rosa de los vientos (NORTE) — esquina inferior izquierda */}
      <g data-testid="vm-planta-a-norte" transform={`translate(${X(0) + 18}, ${Y(LOTE_D) + 54})`}>
        <circle r="15" fill="rgba(34,211,238,0.05)" stroke={CYAN} strokeWidth="0.6" strokeOpacity="0.5" />
        <circle r="10" fill="none" stroke={CYAN} strokeWidth="0.4" strokeOpacity="0.35" />
        <g stroke={CYAN} strokeWidth="0.5" opacity="0.5">
          <line x1="0" y1="0" x2="6.5" y2="6.5" /><line x1="0" y1="0" x2="-6.5" y2="6.5" />
          <line x1="0" y1="0" x2="6.5" y2="-6.5" /><line x1="0" y1="0" x2="-6.5" y2="-6.5" />
        </g>
        <path d="M0 -13 L3.4 0 L0 -3 L-3.4 0 Z" fill={CYAN_HOT} stroke="none" filter="url(#maGlow)" />
        <path d="M0 13 L3.4 0 L0 3 L-3.4 0 Z" fill="rgba(148,163,184,0.55)" stroke="none" />
        <text x="0" y="-17" textAnchor="middle" fill={CYAN} fontSize="7" fontWeight="800" stroke="none">N</text>
        <text x="0" y="26" textAnchor="middle" fill="#64748b" fontSize="5" fontWeight="700" stroke="none">NORTE 0.00°</text>
      </g>

      <text x={X(0)} y={VH - 22} fill="#64748b" fontSize="7" fontWeight="700">MODELO A · DISTRIB. {variant} {variant === "02" ? "(CONTEMPORÁNEA · PLANTA ABIERTA)" : "(FUNCIONAL)"} · {uso} · EJE PASADIZO CENTRAL · MUROS 0.25/0.15 m · ESC 1:100</text>
      <text x={X(0)} y={VH - 11} fill={accent || "#22d3ee"} fontSize="7" fontWeight="800" data-testid="vm-planta-a-techada">ÁREA TECHADA DIBUJADA (Σ unidades): {n2(model.techada)} m²</text>

      {(Math.abs((Number(anchoReal) || LOTE_W) - LOTE_W) > 0.05 || Math.abs((Number(largoReal) || LOTE_D) - LOTE_D) > 0.05) && (
        <g data-testid="vm-planta-a-aviso">
          <rect x={X(LOTE_W) - 3.0 * sc} y={y0 - 39} width={3.0 * sc} height={11} rx="2.5" fill="rgba(245,158,11,0.14)" stroke="#f59e0b" strokeWidth="0.6" />
          <text x={X(LOTE_W) - 1.5 * sc} y={y0 - 31} textAnchor="middle" fill="#fbbf24" fontSize="5.6" fontWeight="800">REF. CURADA 7.00×20.00 · TERR. {n2(anchoReal)}×{n2(largoReal)}</text>
        </g>
      )}
      {/* ── BANNER DE ALERTA DE EXCESO (>100%): sobrepuesto al frente del lienzo ── */}
      {areaExcedida && (
        <g data-testid="vm-area-excedida-banner">
          <rect x={VW / 2 - 168} y={13} width={336} height={26} rx={5} fill="#dc2626" stroke="#fca5a5" strokeWidth="1.3" />
          <text x={VW / 2} y={30} textAnchor="middle" fill="#ffffff" fontSize="10.5" fontWeight="900" letterSpacing="0.3">⚠ ERROR: ÁREA EXCEDIDA - REVISE AMBIENTES</text>
          <text x={VW / 2} y={51} textAnchor="middle" fill="#fecaca" fontSize="8" fontWeight="800">Utilización {n2(utilPct)}% del terreno · reduzca cantidades o áreas</text>
        </g>
      )}
    </svg>
  );
}
